import React, { useEffect, useMemo, useState, createContext } from 'react';
import { cn } from '../../lib/utils';
import { useMotionValue, motion, useMotionTemplate, AnimatePresence } from 'framer-motion';
// Create a context to track dialog open state
export const DialogContext = createContext<{
  isDialogOpen: boolean;
}>({
  isDialogOpen: false
});
// Floating geometric shapes component
const FloatingShapes = () => {
  const shapes = useMemo(() => [
  // Large circle outlines
  {
    type: 'circle',
    cx: '8%',
    cy: '45%',
    r: 18,
    opacity: 0.07,
    animClass: 'animate-float-slow'
  }, {
    type: 'circle',
    cx: '92%',
    cy: '25%',
    r: 14,
    opacity: 0.05,
    animClass: 'animate-float-medium'
  }, {
    type: 'circle',
    cx: '75%',
    cy: '70%',
    r: 22,
    opacity: 0.06,
    animClass: 'animate-float-slow-reverse'
  }, {
    type: 'circle',
    cx: '20%',
    cy: '80%',
    r: 12,
    opacity: 0.08,
    animClass: 'animate-float-medium'
  }, {
    type: 'circle',
    cx: '50%',
    cy: '15%',
    r: 16,
    opacity: 0.04,
    animClass: 'animate-float-slow'
  }, {
    type: 'circle',
    cx: '35%',
    cy: '55%',
    r: 10,
    opacity: 0.06,
    animClass: 'animate-float-medium-reverse'
  },
  // Small dots (filled)
  {
    type: 'dot',
    cx: '12%',
    cy: '35%',
    r: 3,
    opacity: 0.12,
    animClass: 'animate-float-medium'
  }, {
    type: 'dot',
    cx: '88%',
    cy: '60%',
    r: 2.5,
    opacity: 0.1,
    animClass: 'animate-float-slow-reverse'
  }, {
    type: 'dot',
    cx: '45%',
    cy: '85%',
    r: 2,
    opacity: 0.08,
    animClass: 'animate-float-slow'
  }, {
    type: 'dot',
    cx: '70%',
    cy: '20%',
    r: 3,
    opacity: 0.1,
    animClass: 'animate-float-medium-reverse'
  }, {
    type: 'dot',
    cx: '25%',
    cy: '10%',
    r: 2,
    opacity: 0.09,
    animClass: 'animate-float-medium'
  }, {
    type: 'dot',
    cx: '60%',
    cy: '45%',
    r: 2.5,
    opacity: 0.07,
    animClass: 'animate-float-slow'
  },
  // Squares (outlined, some rotated)
  {
    type: 'square',
    cx: '85%',
    cy: '40%',
    size: 24,
    rotation: 15,
    opacity: 0.05,
    animClass: 'animate-float-slow'
  }, {
    type: 'square',
    cx: '15%',
    cy: '65%',
    size: 18,
    rotation: 45,
    opacity: 0.06,
    animClass: 'animate-float-medium-reverse'
  }, {
    type: 'square',
    cx: '55%',
    cy: '30%',
    size: 20,
    rotation: -10,
    opacity: 0.04,
    animClass: 'animate-float-medium'
  }, {
    type: 'square',
    cx: '40%',
    cy: '75%',
    size: 16,
    rotation: 30,
    opacity: 0.05,
    animClass: 'animate-float-slow-reverse'
  },
  // Diagonal lines
  {
    type: 'line',
    x1: '5%',
    y1: '20%',
    length: 40,
    angle: -30,
    opacity: 0.06,
    animClass: 'animate-float-slow'
  }, {
    type: 'line',
    x1: '78%',
    y1: '50%',
    length: 35,
    angle: 45,
    opacity: 0.04,
    animClass: 'animate-float-medium-reverse'
  }, {
    type: 'line',
    x1: '30%',
    y1: '90%',
    length: 30,
    angle: -45,
    opacity: 0.05,
    animClass: 'animate-float-medium'
  }, {
    type: 'line',
    x1: '65%',
    y1: '8%',
    length: 25,
    angle: 30,
    opacity: 0.05,
    animClass: 'animate-float-slow-reverse'
  }], []);
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-102">
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" data-id="element-103">
        {shapes.map((shape, i) => {
        if (shape.type === 'circle') {
          return <circle key={`circle-${i}`} cx={shape.cx} cy={shape.cy} r={shape.r} fill="none" stroke="rgba(148, 180, 214, 1)" strokeWidth="1" opacity={shape.opacity} className={shape.animClass} data-id="element-104" />;
        }
        if (shape.type === 'dot') {
          return <circle key={`dot-${i}`} cx={shape.cx} cy={shape.cy} r={shape.r} fill="rgba(148, 180, 214, 1)" opacity={shape.opacity} className={shape.animClass} data-id="element-105" />;
        }
        if (shape.type === 'square') {
          return <rect key={`square-${i}`} x={shape.cx} y={shape.cy} width={shape.size} height={shape.size} fill="none" stroke="rgba(148, 180, 214, 1)" strokeWidth="1" opacity={shape.opacity} transform={`rotate(${shape.rotation}, ${shape.cx}, ${shape.cy})`} className={shape.animClass} data-id="element-106" />;
        }
        if (shape.type === 'line') {
          const rad = (shape.angle || 0) * Math.PI / 180;
          const len = shape.length || 30;
          return <line key={`line-${i}`} x1={shape.x1} y1={shape.y1} x2={`calc(${shape.x1} + ${Math.cos(rad) * len}px)`} y2={`calc(${shape.y1} + ${Math.sin(rad) * len}px)`} stroke="rgba(148, 180, 214, 1)" strokeWidth="1" opacity={shape.opacity} className={shape.animClass} data-id="element-107" />;
        }
        return null;
      })}
      </svg>
      {/* Additional floating shapes using divs for better animation control */}
      <div className="animate-float-slow absolute" style={{
      left: '7%',
      top: '42%',
      opacity: 0.07
    }} data-id="element-108">
        <div className="w-9 h-9 rounded-full border border-[#94b4d6]" data-id="element-109" />
      </div>
      <div className="animate-float-medium absolute" style={{
      left: '90%',
      top: '22%',
      opacity: 0.05
    }} data-id="element-110">
        <div className="w-7 h-7 rounded-full border border-[#94b4d6]" data-id="element-111" />
      </div>
      <div className="animate-float-slow-reverse absolute" style={{
      left: '73%',
      top: '68%',
      opacity: 0.06
    }} data-id="element-112">
        <div className="w-11 h-11 rounded-full border border-[#94b4d6]" data-id="element-113" />
      </div>
      <div className="animate-float-medium absolute" style={{
      left: '18%',
      top: '78%',
      opacity: 0.08
    }} data-id="element-114">
        <div className="w-6 h-6 rounded-full border border-[#94b4d6]" data-id="element-115" />
      </div>
      <div className="animate-float-medium-reverse absolute" style={{
      left: '48%',
      top: '12%',
      opacity: 0.05
    }} data-id="element-116">
        <div className="w-8 h-8 rounded-full border border-[#94b4d6]" data-id="element-117" />
      </div>

      {/* Outlined squares */}
      <div className="animate-float-slow absolute" style={{
      left: '83%',
      top: '38%',
      opacity: 0.05
    }} data-id="element-118">
        <div className="w-6 h-6 border border-[#94b4d6] rotate-[15deg]" data-id="element-119" />
      </div>
      <div className="animate-float-medium-reverse absolute" style={{
      left: '13%',
      top: '62%',
      opacity: 0.06
    }} data-id="element-120">
        <div className="w-5 h-5 border border-[#94b4d6] rotate-45" data-id="element-121" />
      </div>
      <div className="animate-float-medium absolute" style={{
      left: '53%',
      top: '28%',
      opacity: 0.04
    }} data-id="element-122">
        <div className="w-5 h-5 border border-[#94b4d6] -rotate-[10deg]" data-id="element-123" />
      </div>
      <div className="animate-float-slow-reverse absolute" style={{
      left: '38%',
      top: '73%',
      opacity: 0.05
    }} data-id="element-124">
        <div className="w-4 h-4 border border-[#94b4d6] rotate-[30deg]" data-id="element-125" />
      </div>

      {/* Small dots */}
      <div className="animate-float-medium absolute" style={{
      left: '10%',
      top: '33%',
      opacity: 0.12
    }} data-id="element-126">
        <div className="w-1.5 h-1.5 rounded-full bg-[#94b4d6]" data-id="element-127" />
      </div>
      <div className="animate-float-slow-reverse absolute" style={{
      left: '86%',
      top: '58%',
      opacity: 0.1
    }} data-id="element-128">
        <div className="w-1 h-1 rounded-full bg-[#94b4d6]" data-id="element-129" />
      </div>
      <div className="animate-float-slow absolute" style={{
      left: '43%',
      top: '83%',
      opacity: 0.08
    }} data-id="element-130">
        <div className="w-1 h-1 rounded-full bg-[#94b4d6]" data-id="element-131" />
      </div>
      <div className="animate-float-medium-reverse absolute" style={{
      left: '68%',
      top: '18%',
      opacity: 0.1
    }} data-id="element-132">
        <div className="w-1.5 h-1.5 rounded-full bg-[#94b4d6]" data-id="element-133" />
      </div>
      <div className="animate-float-medium absolute" style={{
      left: '23%',
      top: '8%',
      opacity: 0.09
    }} data-id="element-134">
        <div className="w-1 h-1 rounded-full bg-[#94b4d6]" data-id="element-135" />
      </div>

      {/* Diagonal lines */}
      <div className="animate-float-slow absolute" style={{
      left: '4%',
      top: '18%',
      opacity: 0.06
    }} data-id="element-136">
        <div className="w-10 h-px bg-[#94b4d6] -rotate-[30deg]" data-id="element-137" />
      </div>
      <div className="animate-float-medium-reverse absolute" style={{
      left: '76%',
      top: '48%',
      opacity: 0.04
    }} data-id="element-138">
        <div className="w-9 h-px bg-[#94b4d6] rotate-45" data-id="element-139" />
      </div>
      <div className="animate-float-medium absolute" style={{
      left: '28%',
      top: '88%',
      opacity: 0.05
    }} data-id="element-140">
        <div className="w-8 h-px bg-[#94b4d6] -rotate-45" data-id="element-141" />
      </div>
      <div className="animate-float-slow-reverse absolute" style={{
      left: '63%',
      top: '6%',
      opacity: 0.05
    }} data-id="element-142">
        <div className="w-7 h-px bg-[#94b4d6] rotate-[30deg]" data-id="element-143" />
      </div>
    </div>;
};
export const HeroHighlight = ({
  children,
  className,
  containerClassName
}: {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
}) => {
  let mouseX = useMotionValue(0);
  let mouseY = useMotionValue(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  useEffect(() => {
    if (!isMobile) return;
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const documentHeight = document.body.scrollHeight;
      const videoSectionOffset = viewportHeight * 0.15;
      const adjustedScrollY = Math.max(0, scrollY - videoSectionOffset);
      const adjustedDocumentHeight = documentHeight - videoSectionOffset;
      const scrollPercentY = Math.min(adjustedScrollY / (adjustedDocumentHeight - viewportHeight), 1);
      mouseX.set(window.innerWidth / 2);
      mouseY.set(videoSectionOffset + scrollPercentY * (documentHeight - viewportHeight));
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile, mouseX, mouseY]);
  // Check for dialog open state
  useEffect(() => {
    const checkForDialog = () => {
      const openDialogs = document.querySelectorAll('[aria-modal="true"]');
      setIsDialogOpen(openDialogs.length > 0);
    };
    checkForDialog();
    const observer = new MutationObserver(checkForDialog);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal']
    });
    return () => observer.disconnect();
  }, []);
  function handleMouseMove({
    currentTarget,
    clientX,
    clientY
  }: React.MouseEvent<HTMLDivElement>) {
    if (isMobile || isDialogOpen) return;
    if (!currentTarget) return;
    let {
      left,
      top
    } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }
  return <DialogContext.Provider value={{
    isDialogOpen
  }} data-id="element-144">
      <AnimatePresence data-id="element-145">
        <motion.div className={cn('relative min-h-screen flex items-start bg-[#3d5a80] justify-center w-full group', containerClassName)} onMouseMove={handleMouseMove} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} data-id="element-146">
          {/* Subtle radial gradient for depth */}
          <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 50% 30%, rgba(70, 110, 160, 0.25) 0%, transparent 70%)'
        }} data-id="element-147" />
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.07]" style={{
          backgroundImage: `
                linear-gradient(rgba(180, 210, 240, 0.5) 1px, transparent 1px),
                linear-gradient(90deg, rgba(180, 210, 240, 0.5) 1px, transparent 1px)
              `,
          backgroundSize: '40px 40px'
        }} data-id="element-148" />

          {/* Floating geometric shapes */}
          <FloatingShapes data-id="element-149" />

          {/* Mouse-follow subtle glow (desktop only) */}
          <motion.div className={cn('pointer-events-none absolute inset-0', isMobile || isDialogOpen ? 'hidden' : 'opacity-0 transition duration-300 group-hover:opacity-100')} style={{
          background: 'transparent',
          WebkitMaskImage: useMotionTemplate`
                radial-gradient(
                  300px circle at ${mouseX}px ${mouseY}px,
                  white,
                  transparent
                )
              `,
          maskImage: useMotionTemplate`
                radial-gradient(
                  300px circle at ${mouseX}px ${mouseY}px,
                  white,
                  transparent
                )
              `,
          backgroundColor: 'rgba(80, 130, 180, 0.12)'
        }} data-id="element-150" />
          <div className={cn('relative z-20 w-full', className)} data-id="element-151">
            {children}
          </div>
        </motion.div>
      </AnimatePresence>
    </DialogContext.Provider>;
};
export const Highlight = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <motion.span initial={{
    backgroundSize: '0% 100%'
  }} whileInView={{
    backgroundSize: '100% 100%'
  }} viewport={{
    once: true,
    margin: '-10% 0px'
  }} transition={{
    duration: 1.5,
    ease: 'linear',
    delay: 0.2
  }} style={{
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left center',
    display: 'inline'
  }} className={cn('relative inline-block pb-1 px-1 rounded-lg bg-gradient-to-r from-blue-400/80 to-indigo-600/80', className)} data-id="element-152">
      {children}
    </motion.span>;
};